import React, { useState } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { FaCalendarAlt, FaTimes, FaSave } from 'react-icons/fa';
import styles from './ExitDateModal.module.css';

const ExitDateModal = ({ selectedEmployee, onClose, onUpdate, onEmployeeUpdate }) => {
  const { isDarkMode } = useTheme();
  const [exitDate, setExitDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Çalışanı tüm projelerden pasif duruma geçir
  const updateEmployeeStatusInProjects = async (employeeId, targetStatus = 'passive') => {
    try {
      // Çalışanın mevcut proje atamalarını al
      const employeeRef = doc(db, 'Employees', employeeId);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) return;
      
      const employeeData = employeeDoc.data();
      const projects = employeeData.projects || [];
      
      // Çalışanın atandığı tüm projeleri güncelle
      const updatePromises = [];
      
      // Benzersiz proje ID'lerini bul
      const uniqueProjectIds = [...new Set(projects.map(p => p.id))];
      
      for (const projectId of uniqueProjectIds) {
        const projectRef = doc(db, 'Projects', projectId);
        const projectDoc = await getDoc(projectRef);
        
        if (projectDoc.exists()) {
          const projectData = projectDoc.data();
          const projectEmployees = projectData.employees || [];
          
          // Bu çalışanın tüm girişlerini hedef duruma geçir
          const updatedEmployees = projectEmployees.map(emp => {
            if (emp.id === employeeId) {
              return {
                ...emp,
                status: targetStatus
              };
            }
            return emp;
          });
          
          // Eğer değişiklik varsa güncelle
          const hasChanges = projectEmployees.some(emp => emp.id === employeeId && emp.status !== targetStatus);
          if (hasChanges) {
            updatePromises.push(
              updateDoc(projectRef, { employees: updatedEmployees })
            );
          }
        }
      }
      
      // Çalışanın kendi projects dizisindeki durumları da güncelle
      const updatedProjects = projects.map(proj => ({
        ...proj,
        status: targetStatus
      }));
      
      updatePromises.push(
        updateDoc(employeeRef, { projects: updatedProjects })
      );
      
      await Promise.all(updatePromises);
      
      console.log(`Çalışan ${employeeId} tüm projelerden ${targetStatus} duruma geçirildi`);
    } catch (error) {
      console.error('Error updating employee status in projects:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!exitDate) {
      toast.error('Lütfen işten ayrılma tarihini seçin');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const employeeRef = doc(db, 'Employees', selectedEmployee.id);
      
      // Önce çalışanı tüm projelerden pasif yap
      await updateEmployeeStatusInProjects(selectedEmployee.id, 'passive');
      
      // Sonra çalışanın durumunu güncelle
      await updateDoc(employeeRef, { 
        Active: 0,
        employeeExitDate: exitDate
      });
      
      // Dinamik olarak güncellenen çalışan bilgisini geri gönder
      const updatedEmployee = { 
        ...selectedEmployee, 
        Active: 0, 
        employeeExitDate: exitDate 
      };
      
      // Eğer onEmployeeUpdate fonksiyonu varsa, güncellenmiş çalışan bilgisini gönder
      if (onEmployeeUpdate) {
        onEmployeeUpdate(updatedEmployee);
      }
      
      toast.success('Çalışan durumu İnaktif olarak güncellendi, işten ayrılma tarihi kaydedildi ve tüm projelerden pasif duruma geçirildi');
      await onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating employee status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`${styles.modalOverlay} ${isDarkMode ? styles.darkMode : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`${styles.modalContent} ${isDarkMode ? styles.darkMode : ''}`}>
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <FaCalendarAlt className={styles.headerIcon} />
            <h2>İşten Ayrılma Tarihi</h2>
          </div>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="exitDate" className={styles.label}>
              {selectedEmployee?.eName} {selectedEmployee?.eSurname} çalışanının işten ayrılma tarihi:
            </label>
            <input
              type="date"
              id="exitDate"
              value={exitDate}
              onChange={(e) => setExitDate(e.target.value)}
              className={styles.dateInput}
              required
            />
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={onClose}
              className={`${styles.button} ${styles.cancelButton}`}
              disabled={isSubmitting}
            >
              <FaTimes />
              İptal
            </button>
            <button
              type="submit"
              className={`${styles.button} ${styles.saveButton}`}
              disabled={isSubmitting}
            >
              <FaSave />
              {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExitDateModal;
